import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class FreeGiftEngine extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | null} */
  #progressLayoutFrame = null;

  connectedCallback() {
    this.#setupProgressLayout();
    this.syncGiftLines();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();

    if (this.#progressLayoutFrame) {
      cancelAnimationFrame(this.#progressLayoutFrame);
      this.#progressLayoutFrame = null;
    }
  }

  async syncGiftLines() {
    const configElement = this.querySelector('[data-free-gift-engine-config]');
    if (!(configElement instanceof HTMLScriptElement) || !configElement.textContent) return;
    if (window.__freeGiftEngineSyncInFlight) return;

    let config;

    try {
      config = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Failed to parse free gift config', error);
      return;
    }

    const updates = this.#buildUpdates(config);
    if (Object.keys(updates).length === 0) return;

    window.__freeGiftEngineSyncInFlight = true;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
        .filter(Boolean);

      const body = JSON.stringify({
        updates,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname + window.location.search,
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abortController.signal,
      });

      const responseText = await response.text();
      const cart = JSON.parse(responseText);

      if (cart.errors) {
        console.error('Failed to sync free gift lines', cart.errors);
        return;
      }

      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id || 'free-gift-engine', {
          source: 'free-gift-engine',
          sections: cart.sections,
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Failed to sync free gift lines', error);
    } finally {
      window.__freeGiftEngineSyncInFlight = false;
      this.#abortController = null;
    }
  }

  /**
   * @param {{
   *   cartTotal: number,
   *   groups: Array<{ threshold: number, productIds: number[] }>,
   *   giftLines: Array<{ key: string, threshold: number | null, productId: number, quantity: number }>
   * }} config
   */
  #buildUpdates(config) {
    /** @type {Record<string, number>} */
    const updates = {};
    /** @type {Map<string, { threshold: number, productIds: number[] }>} */
    const groupedLines = new Map();

    config.giftLines.forEach((line) => {
      const matchingGroup = config.groups.find((group) => {
        return (
          String(group.threshold) === String(line.threshold) || group.productIds.includes(Number(line.productId))
        );
      });

      if (!matchingGroup) {
        updates[line.key] = 0;
        return;
      }

      const groupKey = String(matchingGroup.threshold);
      if (!groupedLines.has(groupKey)) {
        groupedLines.set(groupKey, matchingGroup);
      }
    });

    groupedLines.forEach((group) => {
      const matchingLines = config.giftLines.filter((line) => {
        return (
          String(group.threshold) === String(line.threshold) || group.productIds.includes(Number(line.productId))
        );
      });

      if (config.cartTotal < group.threshold) {
        matchingLines.forEach((line) => {
          updates[line.key] = 0;
        });
        return;
      }

      const [lineToKeep, ...linesToRemove] = matchingLines;
      if (!lineToKeep) return;

      if (lineToKeep.quantity !== 1) {
        updates[lineToKeep.key] = 1;
      }

      linesToRemove.forEach((line) => {
        updates[line.key] = 0;
      });
    });

    return updates;
  }

  #setupProgressLayout() {
    const progressTrack = this.querySelector('.cart-free-gifts__progress-track');
    if (!(progressTrack instanceof HTMLElement)) return;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#queueProgressLayout();
    });
    this.#resizeObserver.observe(progressTrack);

    this.#queueProgressLayout();

    document.fonts?.ready
      ?.then(() => {
        this.#queueProgressLayout();
      })
      .catch(() => {});
  }

  #queueProgressLayout() {
    if (this.#progressLayoutFrame) {
      cancelAnimationFrame(this.#progressLayoutFrame);
    }

    this.#progressLayoutFrame = requestAnimationFrame(() => {
      this.#progressLayoutFrame = null;
      this.#layoutProgressSteps();
    });
  }

  #layoutProgressSteps() {
    const progressTrack = this.querySelector('.cart-free-gifts__progress-track');
    if (!(progressTrack instanceof HTMLElement)) return;

    const steps = Array.from(progressTrack.querySelectorAll('.cart-drawer__progress-step')).filter(
      (step) => step instanceof HTMLElement
    );
    if (steps.length === 0) return;

    const trackWidth = progressTrack.clientWidth;
    if (!trackWidth) return;

    const orderedSteps = steps
      .map((element, index) => {
        const stopPercent = Number.parseFloat(element.dataset.progressStop ?? '0');

        return {
          align: element.dataset.progressAlign ?? 'center',
          element,
          index,
          stopPercent: Number.isFinite(stopPercent) ? stopPercent : 0,
          width: element.offsetWidth,
        };
      })
      .sort((stepA, stepB) => {
        if (stepA.stopPercent === stepB.stopPercent) {
          return stepA.index - stepB.index;
        }

        return stepA.stopPercent - stepB.stopPercent;
      });

    const desiredGap = 8;
    const totalStepWidth = orderedSteps.reduce((sum, step) => sum + step.width, 0);
    const maxPossibleGap =
      orderedSteps.length > 1 ? Math.max((trackWidth - totalStepWidth) / (orderedSteps.length - 1), 0) : 0;
    const gap = orderedSteps.length > 1 ? Math.min(desiredGap, maxPossibleGap) : 0;
    const centers = new Array(orderedSteps.length);

    for (let index = orderedSteps.length - 1; index >= 0; index -= 1) {
      const step = orderedSteps[index];
      const minCenter = step.width / 2;
      const maxCenter =
        index === orderedSteps.length - 1
          ? trackWidth - step.width / 2
          : centers[index + 1] - (step.width / 2 + orderedSteps[index + 1].width / 2 + gap);
      const idealCenter =
        step.align === 'end' ? trackWidth - step.width / 2 : (trackWidth * step.stopPercent) / 100;

      centers[index] = Math.max(minCenter, Math.min(idealCenter, maxCenter));
    }

    orderedSteps.forEach((step, index) => {
      step.element.style.left = `${centers[index]}px`;
      step.element.style.right = 'auto';
    });
  }
}

if (!customElements.get('free-gift-engine')) {
  customElements.define('free-gift-engine', FreeGiftEngine);
}
